;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:dotorg-next":true});}catch(e){}}();import type { FakeStore, OwnedMembership, UserFakeDoc } from './store/types'

function jsonApiResponse(body: unknown, init: ResponseInit = {}): Response {
  return new Response(JSON.stringify(body), {
    ...init,
    headers: { 'Content-Type': 'application/vnd.api+json', ...(init.headers || {}) },
  })
}

function unauthorized(): Response {
  return jsonApiResponse({ errors: [{ detail: 'Unauthorized' }] }, { status: 401 })
}

function hasBasicAuth(request: Request): boolean {
  const header = request.headers.get('authorization') ?? ''
  return header.startsWith('Basic ')
}

interface FoundMembership {
  pid: string;
  doc: UserFakeDoc;
  membership: OwnedMembership;
}

// Resolves an mvaultId to its owning fake doc. Returns null when the id is
// unknown to fake state or the owner's fakes are switched off. Callers
// translate that into "fall through to the real upstream", because the user
// may well be acting on a *real* membership.
async function findByMvaultId(store: FakeStore, mvaultId: string): Promise<FoundMembership | null> {
  const pid = await store.findPidByMvaultId(mvaultId)
  if(!pid) return null

  const doc = await store.getUserDoc(pid)
  if(!doc || !doc.enabled) return null

  const membership = doc.memberships.find(m => m.data.mvault_id === mvaultId)
  if(!membership) return null

  return { pid, doc, membership }
}

interface SubscriptionPatchBody {
  data?: {
    type?: string;
    id?: string;
    attributes?: {
      status?: string;
      amount?: number;
    };
  };
}

// Mirrors TVault's SubscriptionMvaultIdViewSet.partial_update: the same PATCH
// endpoint handles both cancellation (attributes.status === 'canceled') and
// amount updates (attributes.amount). The real view branches via
// `if "amount" in request.data` and routes to handle_amount_update from
// SubscriptionAmountUpdateMixin; otherwise UpdateSubscriptionSerializer runs.
async function partialUpdateSubscriptionByMvaultId(request: Request, callSign: string, mvaultId: string, store: FakeStore): Promise<Response | null> {
  // Look up BEFORE touching the body: an unknown mvaultId means this request
  // targets a real membership and must fall through untouched.
  const found = await findByMvaultId(store, mvaultId)
  if(!found) return null

  if(!hasBasicAuth(request)) return unauthorized()

  const { pid, doc, membership } = found
  if(membership.data.station_info.station_call_sign !== callSign) {
    // Real TVault would 404 because the station_filter excludes this row from the queryset
    return jsonApiResponse({ errors: [{ detail: 'Subscription not found for station' }] }, { status: 404 })
  }

  let body: SubscriptionPatchBody = {}
  try {
    body = await request.json() as SubscriptionPatchBody
  } catch {
    return jsonApiResponse({ errors: [{ detail: 'Invalid JSON body' }] }, { status: 400 })
  }

  if(body?.data?.type !== 'Subscription') {
    return jsonApiResponse({ errors: [{ detail: 'data.type must be Subscription' }] }, { status: 400 })
  }

  const attrs = body.data.attributes ?? {}

  // Amount-update branch. Real TVault validates 500-10000 cents and requires
  // ACTIVE/PAST_DUE status (UpdateAmountSerializer). The fake only enforces
  // positive-integer cents so manual e2e isn't blocked by the existing
  // monthly-shaped range while we wait on the TVault team's combined endpoint.
  if(attrs.amount !== undefined) {
    if(!Number.isInteger(attrs.amount) || attrs.amount <= 0) {
      return jsonApiResponse({ errors: [{ detail: 'amount must be a positive integer (cents)' }] }, { status: 400 })
    }
    if(membership.data.donation_info && 'donation_amount' in membership.data.donation_info) {
      membership.data.donation_info.donation_amount = attrs.amount
      await store.putUserDoc(pid, doc)
    }
    return jsonApiResponse({
      data: {
        type: 'Subscription',
        id: mvaultId,
        attributes: { amount: attrs.amount },
      },
    })
  }

  // Cancel branch.
  if(attrs.status !== 'canceled') {
    return jsonApiResponse({ errors: [{ detail: `Unsupported status transition: ${attrs.status}` }] }, { status: 400 })
  }

  // Mutate state: flip to expired, disable further self-cancel, and clear
  // next_billing_cycle. Real TVault stops the recurring billing on cancel;
  // without nulling this the UI's isActiveSubscriptionMember check
  // (donation_info.next_billing_cycle !== null) keeps treating the canceled
  // membership as an active subscription.
  membership.data.membership_status = 'expired'
  membership.data.self_cancel = false
  if(membership.data.donation_info && 'next_billing_cycle' in membership.data.donation_info) {
    membership.data.donation_info.next_billing_cycle = null
  }
  await store.putUserDoc(pid, doc)

  return jsonApiResponse({
    data: {
      type: 'Subscription',
      id: mvaultId,
      attributes: { status: 'canceled' },
    },
  })
}

function oneYearFromTodayIso(): string {
  const d = new Date()
  d.setUTCFullYear(d.getUTCFullYear() + 1)
  return d.toISOString().slice(0, 10)
}

// TVault's convert endpoint returns errors as a top-level array of
// `{status, detail}` objects (see tvault/src/tvault/api/utils.py
// _change_one_time_to_yearly). Use this helper to match that shape exactly
// for the convert handler.
function tvaultErrorArray(status: number, detail: string): Response {
  return jsonApiResponse([{ status, detail }], { status })
}

async function convertOneTimeToYearlyByMvaultId(request: Request, callSign: string, mvaultId: string, store: FakeStore): Promise<Response | null> {
  // Unknown mvaultId → the transaction lives in real TVault; fall through.
  const found = await findByMvaultId(store, mvaultId)
  if(!found) return null

  if(!hasBasicAuth(request)) return unauthorized()

  const { pid, doc, membership } = found
  // Real TVault: TransactionNotFoundError -> 404 (transaction lookup is by
  // mvault_id; StationScopeMixin scopes by station). A fake row seeded under
  // a different station 404s the same way.
  if(membership.data.station_info.station_call_sign !== callSign) {
    return tvaultErrorArray(404, 'Transaction not found.')
  }

  const donation = membership.data.donation_info
  // Mirrors TVault's TransactionAlreadyProcessedError / InvalidTransactionStatusError,
  // both of which return 400 from _change_one_time_to_yearly.
  if(!donation || !('donation_frequency' in donation)) {
    return tvaultErrorArray(400, 'Transaction was not successful. Stripe customer cannot be retrieved.')
  }
  if(donation.donation_frequency !== 'One-time') {
    return tvaultErrorArray(400, 'Transaction already attached to a subscription.')
  }

  // Mutate to a yearly subscription. Real TVault creates a new Subscription
  // record and links it to the transaction; the fake just assigns a synthetic
  // subscription_id and flips the membership fields the UI reads.
  donation.donation_frequency = 'Yearly'
  donation.next_billing_cycle = oneYearFromTodayIso()
  donation.display_donate_yearly = false
  donation.display_donation_amount_update = true
  donation.subscription_id = donation.subscription_id ?? `sub-converted-${mvaultId}`
  donation.customer_id = donation.customer_id ?? `cust-converted-${mvaultId}`
  membership.data.self_cancel = true
  await store.putUserDoc(pid, doc)

  // Real TVault returns 201 with a serialized Subscription (all fields). The
  // dotorg-next client doesn't read the body, but match status + minimal shape
  // for fidelity.
  return jsonApiResponse({
    mvault_id: mvaultId,
    amount: donation.donation_amount,
    interval: 'Yearly',
    status: 'active',
    gateway_type: donation.payment_gateway?.type,
    pbs_membership_token: membership.data.token,
  }, { status: 201 })
}

interface CreateTransactionBody {
  data?: {
    type?: string;
    attributes?: {
      amount?: number;
      gateway_type?: string;
    };
    relationships?: {
      customer?: {
        data?: {
          first_name?: string;
          last_name?: string;
          email?: string;
          pid?: string;
        };
      };
    };
  };
}

function randomId(prefix: string): string {
  return `${prefix}-${Math.random().toString(36).slice(2, 10)}`
}

async function createOneTimeTransaction(request: Request, callSign: string, store: FakeStore): Promise<Response | null> {
  if(!hasBasicAuth(request)) return unauthorized()

  let body: CreateTransactionBody = {}
  try {
    body = await request.json() as CreateTransactionBody
  } catch {
    return tvaultErrorArray(400, 'Invalid JSON body')
  }

  const pid = body?.data?.relationships?.customer?.data?.pid
  const email = body?.data?.relationships?.customer?.data?.email
  const amountCents = body?.data?.attributes?.amount
  const gatewayType = body?.data?.attributes?.gateway_type ?? 'stripe'

  if(!pid || !email) {
    return tvaultErrorArray(400, 'customer.pid and customer.email are required')
  }

  // Only fake the create when this user is already in fake-land; otherwise
  // the Dev Management "create one-time donation" tool should keep hitting
  // real staging TVault. (Body consumption is safe: the fall-through path
  // re-issues the request from the original route + init, not this Request.)
  const doc = await store.getUserDoc(pid)
  if(!doc || !doc.enabled) return null

  if(!Number.isInteger(amountCents) || (amountCents as number) <= 0) {
    return tvaultErrorArray(400, 'attributes.amount must be a positive integer (cents)')
  }

  const mvaultId = randomId('mv-dev-onetime')
  const transactionId = randomId('txn-dev-onetime')
  const customerId = randomId('cust-dev-onetime')
  const token = randomId('tok-dev-onetime')
  const stationId = `station-uuid-${callSign}`
  const nowIso = new Date().toISOString()

  doc.memberships.push({
    pid,
    email,
    data: {
      membership_status: 'active',
      member_since: nowIso,
      membership_expiration_date: oneYearFromTodayIso(),
      membership_id: randomId('mid'),
      mvault_id: mvaultId,
      token,
      self_cancel: false,
      station_info: {
        station_id: stationId,
        station_short_common_name: `${callSign} PBS`,
        station_call_sign: callSign,
        contact_station_email: null,
        contact_station_phone: null,
      },
      donation_info: {
        donation_frequency: 'One-time',
        donation_amount: amountCents as number,
        display_donation_amount_update: false,
        next_billing_cycle: null,
        subscription_id: null,
        customer_id: customerId,
        activation_link: null,
        display_donate_yearly: true,
        minimum_annual_amount: 9600,
        minimum_monthly_amount: 800,
        maximum_annual_amount: 1000000,
        maximum_monthly_amount: 1000000,
        payment_gateway: {
          type: gatewayType as 'stripe' | 'amazon_pay' | 'xfinity' | 'spreedly',
          platform: 'Stripe',
          payment_details: {
            payment_method: 'credit_card',
            brand: 'visa',
            card_last4: '4242',
            card_expiration: '12/2030',
            card_update_url: null,
          },
        },
      },
      transactions: [
        { date: nowIso, amount: amountCents as number, status: 'successful' },
      ],
    },
  })
  await store.putUserDoc(pid, doc)

  // Mirror TransactionViewSet.create's 201 envelope. dotorg-next's
  // createDevOneTimeTransaction reads id, attributes.mvault_id,
  // attributes.pbs_membership_token, attributes.status, and
  // relationships.station.data.id from this body.
  return jsonApiResponse({
    data: {
      id: transactionId,
      type: 'Transaction',
      attributes: {
        mvault_id: mvaultId,
        pbs_membership_token: token,
        status: 'successful',
      },
      relationships: {
        station: { data: { id: stationId, type: 'Station' } },
      },
    },
  }, { status: 201 })
}

// Public dispatcher. Paths are relative to the versioned API root (i.e. what
// comes after TVAULT_ENDPOINT's `/api/v1`). Returns null when the request
// doesn't match a fake TVault route or fake state doesn't claim it. null
// means "not faked, use the real upstream".
export function handle(request: Request, _url: URL, pathBelowMount: string, store: FakeStore): Promise<Response | null> | null {
  // PATCH /{station}/subscriptions/by_mvault_id/{mvaultId}/  handles
  // both cancel (attributes.status) and amount-update (attributes.amount).
  const patchSubMatch = pathBelowMount.match(/^\/([^/]+)\/subscriptions\/by_mvault_id\/([^/]+)\/?$/)
  if(patchSubMatch && request.method === 'PATCH') {
    const [, callSign, mvaultId] = patchSubMatch
    return partialUpdateSubscriptionByMvaultId(request, callSign, mvaultId, store)
  }

  // POST /{station}/transactions/by_mvault_id/{mvaultId}/convert-one-time-to-yearly/
  const convertMatch = pathBelowMount.match(/^\/([^/]+)\/transactions\/by_mvault_id\/([^/]+)\/convert-one-time-to-yearly\/?$/)
  if(convertMatch && request.method === 'POST') {
    const [, callSign, mvaultId] = convertMatch
    return convertOneTimeToYearlyByMvaultId(request, callSign, mvaultId, store)
  }

  // POST /{station}/transactions/
  const createTransactionMatch = pathBelowMount.match(/^\/([^/]+)\/transactions\/?$/)
  if(createTransactionMatch && request.method === 'POST') {
    const [, callSign] = createTransactionMatch
    return createOneTimeTransaction(request, callSign, store)
  }

  return null
}
